
import React, { useState } from "react";
import { FaEllipsisV, FaTimes } from "react-icons/fa";

import {
  statusColor,
  getDefaultPhoto as utilDefaultPhoto,
} from "./utils";

import ButtonInfo from "../../../components/Button/ButtonInfo";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonManageGroup from "../../../components/Button/ButtonManageGroup";

import ReglementStatusBadge from "../../../components/Adherents/ReglementStatusBadge";

/* ========================================================================== */
/* UTILS                                                                      */
/* ========================================================================== */

const formatDOB = (dateStr) => {
  if (!dateStr) return "—";

  const date = new Date(dateStr);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR");
};

const toAbsolute = (url) => {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) {
    return value;
  }

  const base =
    process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

  return `${base}${value}`;
};

const fallbackPhoto = (adherent, getDefaultPhoto) => {
  if (adherent?.autorisation_photo === false) {
    return "/img/interdiction.jpg";
  }

  if (adherent?.photo_url) {
    return toAbsolute(adherent.photo_url);
  }

  return getDefaultPhoto
    ? getDefaultPhoto(adherent?.genre)
    : utilDefaultPhoto(adherent?.genre);
};

const stop = (event) => {
  event?.stopPropagation?.();
};

const stopAnd = (callback, adherent) => (event) => {
  stop(event);
  callback?.(adherent);
};

/* ========================================================================== */
/* PHOTO                                                                      */
/* ========================================================================== */

function AdherentAvatar({
  adherent,
  getDefaultPhoto,
  size = "md",
}) {
  const photo = fallbackPhoto(adherent, getDefaultPhoto);

  const sizeClass =
    size === "sm" ? "h-10 w-10" : "h-12 w-12";

  return (
    <div
      className={`
        ${sizeClass}
        shrink-0 overflow-hidden rounded-full
        bg-slate-100
        ring-1 ring-slate-200
        dark:bg-slate-800
        dark:ring-slate-700
      `}
    >
      <img
        src={photo}
        alt={`${adherent?.nom || ""} ${adherent?.prenom || ""}`}
        className="h-full w-full object-cover"
        loading="lazy"
        onError={(event) => {
          if (!event.currentTarget.src.endsWith("/img/user_avatar.jpg")) {
            event.currentTarget.src = "/img/user_avatar.jpg";
          }
        }}
      />
    </div>
  );
}

/* ========================================================================== */
/* STATUT INSCRIPTION                                                         */
/* ========================================================================== */

function InscriptionStatusBadge({ statut }) {
  return (
    <span
      className={`
        inline-flex max-w-full items-center
        rounded-lg px-2 py-1
        text-[11px] font-semibold
        ${statusColor(statut)}
      `}
    >
      <span className="truncate">
        {statut || "Sans statut"}
      </span>
    </span>
  );
}

/* ========================================================================== */
/* ACTIONS                                                                    */
/* ========================================================================== */

function AdherentActions({
  adherent,
  isAdmin,
  onRowClick,
  onEdit,
  onManageGroups,
}) {
  return (
    <div className="inline-flex shrink-0 items-center justify-end gap-1.5">
      <ButtonInfo
        onClick={stopAnd(onRowClick, adherent)}
      />

      {isAdmin && (
        <>
          <ButtonEdit
            onClick={stopAnd(onEdit, adherent)}
          />

          <ButtonManageGroup
            onClick={stopAnd(onManageGroups, adherent)}
          />
        </>
      )}
    </div>
  );
}

/* ========================================================================== */
/* COMPOSANT PRINCIPAL                                                        */
/* ========================================================================== */

export default function AdherentsTable({
  adherents = [],
  onRowClick,
  onEdit,
  onManageGroups,
  isAdmin = false,
  getDefaultPhoto,
}) {
  const [openId, setOpenId] = useState(null);

  const toggleMobileActions = (event, id) => {
    stop(event);

    setOpenId((current) =>
      current === id ? null : id
    );
  };

  const closeMobileActions = (event) => {
    stop(event);
    setOpenId(null);
  };

  return (
    <div className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">

      {/* ================================================================ */}
      {/* DESKTOP / TABLETTE                                                */}
      {/* ================================================================ */}

      <div className="hidden w-full min-w-0 overflow-x-auto md:block">
        <table className="w-full table-fixed border-collapse text-left">

          {/* Largeurs contrôlées : total 100 % */}
          <colgroup>
            <col style={{ width: "7%" }} />
            <col style={{ width: "23%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "18%" }} />
          </colgroup>

          <thead className="bg-slate-50 dark:bg-slate-800/80">
            <tr className="border-b border-slate-200 dark:border-slate-700">

              <th className="px-2 py-3 text-center text-[11px] font-bold text-slate-600 dark:text-slate-300">
                Photo
              </th>

              <th className="px-2 py-3 text-[11px] font-bold text-slate-600 dark:text-slate-300">
                Adhérent
              </th>

              <th className="px-2 py-3 text-[11px] font-bold text-slate-600 dark:text-slate-300">
                Naissance
              </th>

              <th className="px-2 py-3 text-[11px] font-bold text-slate-600 dark:text-slate-300">
                Statut
              </th>

              <th className="px-2 py-3 text-[11px] font-bold text-slate-600 dark:text-slate-300">
                Règlement
              </th>

              <th className="px-2 py-3 text-right text-[11px] font-bold text-slate-600 dark:text-slate-300">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">

            {adherents.map((adherent, index) => {
              const nom = String(adherent?.nom || "").toUpperCase();
              const prenom = adherent?.prenom || "";

              return (
                <tr
                  key={adherent.id}
                  onClick={() => onRowClick?.(adherent)}
                  className={`
                    cursor-pointer transition-colors
                    hover:bg-blue-50/70
                    dark:hover:bg-blue-900/20
                    ${
                      index % 2 === 0
                        ? "bg-white dark:bg-slate-900"
                        : "bg-slate-50/70 dark:bg-slate-800/30"
                    }
                  `}
                >

                  {/* PHOTO */}
                  <td className="px-2 py-2.5 align-middle">
                    <div className="flex justify-center">
                      <AdherentAvatar
                        adherent={adherent}
                        getDefaultPhoto={getDefaultPhoto}
                        size="sm"
                      />
                    </div>
                  </td>

                  {/* ADHÉRENT */}
                  <td className="px-2 py-2.5 align-middle">
                    <div className="min-w-0">
                      <p
                        title={nom}
                        className="truncate text-xs font-bold text-slate-900 dark:text-white"
                      >
                        {nom}
                      </p>

                      <p
                        title={prenom}
                        className="truncate text-xs text-slate-600 dark:text-slate-300"
                      >
                        {prenom}
                      </p>
                    </div>
                  </td>

                  {/* DATE NAISSANCE */}
                  <td className="px-2 py-2.5 align-middle">
                    <span className="whitespace-nowrap text-xs text-slate-600 dark:text-slate-300">
                      {formatDOB(adherent?.date_naissance)}
                    </span>
                  </td>

                  {/* STATUT INSCRIPTION */}
                  <td className="px-2 py-2.5 align-middle">
                    <InscriptionStatusBadge
                      statut={adherent?.statut_inscription}
                    />
                  </td>

                  {/* RÈGLEMENT */}
                  <td className="px-2 py-2.5 align-middle">
                    <div className="flex min-w-0">
                      <ReglementStatusBadge
                        statut={adherent?.statut_reglement}
                        compact
                        className="!w-auto max-w-full"
                      />
                    </div>
                  </td>

                  {/* ACTIONS */}
                  <td
                    className="px-2 py-2.5 text-right align-middle"
                    onClick={stop}
                  >
                    <AdherentActions
                      adherent={adherent}
                      isAdmin={isAdmin}
                      onRowClick={onRowClick}
                      onEdit={onEdit}
                      onManageGroups={onManageGroups}
                    />
                  </td>
                </tr>
              );
            })}

            {adherents.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-12 text-center text-sm text-slate-500 dark:text-slate-400"
                >
                  Aucun adhérent à afficher.
                </td>
              </tr>
            )}

          </tbody>
        </table>
      </div>

      {/* ================================================================ */}
      {/* MOBILE                                                            */}
      {/* ================================================================ */}

      <ul className="divide-y divide-slate-200 dark:divide-slate-700 md:hidden">

        {adherents.length === 0 && (
          <li className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
            Aucun adhérent à afficher.
          </li>
        )}

        {adherents.map((adherent) => {
          const nom = String(adherent?.nom || "").toUpperCase();
          const prenom = adherent?.prenom || "";
          const actionsOpen = openId === adherent.id;

          return (
            <li
              key={adherent.id}
              className="relative bg-white dark:bg-slate-900"
            >

              {/* CONTENU */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => onRowClick?.(adherent)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onRowClick?.(adherent);
                  }
                }}
                className={`
                  flex min-w-0 items-start gap-3 p-3
                  transition-colors hover:bg-slate-50
                  dark:hover:bg-slate-800/50
                  ${isAdmin ? "pr-14" : ""}
                `}
              >

                {/* PHOTO */}
                <AdherentAvatar
                  adherent={adherent}
                  getDefaultPhoto={getDefaultPhoto}
                />

                {/* INFORMATIONS */}
                <div className="min-w-0 flex-1 space-y-1.5">

                  {/* NOM */}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                      {nom} {prenom}
                    </p>

                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formatDOB(adherent?.date_naissance)}
                    </p>
                  </div>

                  {/* STATUT INSCRIPTION */}
                  <div className="flex flex-wrap">
                    <InscriptionStatusBadge
                      statut={adherent?.statut_inscription}
                    />
                  </div>

                  {/* RÈGLEMENT */}
                  <div className="flex min-w-0 pt-0.5">
                    <ReglementStatusBadge
                      statut={adherent?.statut_reglement}
                      compact
                      className="!w-auto max-w-full"
                    />
                  </div>

                </div>
              </div>

              {/* MENU MOBILE */}
              {isAdmin && (
                <button
                  type="button"
                  onClick={(event) =>
                    toggleMobileActions(event, adherent.id)
                  }
                  aria-label={
                    actionsOpen
                      ? "Fermer les actions"
                      : "Afficher les actions"
                  }
                  aria-expanded={actionsOpen}
                  className="
                    absolute right-3 top-3 z-10
                    flex h-9 w-9 items-center justify-center
                    rounded-full bg-slate-900 text-white
                    shadow-lg transition
                    hover:bg-slate-700
                    dark:bg-slate-700
                    dark:hover:bg-slate-600
                  "
                >
                  {actionsOpen ? <FaTimes /> : <FaEllipsisV />}
                </button>
              )}

              {/* ACTIONS DÉPLIÉES */}
              {actionsOpen && isAdmin && (
                <div
                  className="border-t border-slate-100 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-800/50"
                  onClick={stop}
                >
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <AdherentActions
                      adherent={adherent}
                      isAdmin={isAdmin}
                      onRowClick={onRowClick}
                      onEdit={onEdit}
                      onManageGroups={onManageGroups}
                    />
                  </div>
                </div>
              )}

            </li>
          );
        })}
      </ul>
    </div>
  );
}
